import type { Answer, Condition, ConditionOperator, DraftField, Field } from '@eformulieren/model'
import { patternMatch } from './pattern.js'
import { isEmpty, rowIsEmpty, rowsOf, valuesOf } from './state.js'

/**
 * De voorwaarden van `zichtbaarAls`: welke operator op welke bron past, en wanneer een voorwaarde klopt
 * (besluit van 31 oktober 2026, eisenboek 12.31, ontwerp 5.6).
 *
 * **Eén plek voor de betekenis.** De evaluator (`visibility.ts`), de poort (`visibilityGaps`) en het
 * proberen in de configurator lezen hier. Dat proberen is dus geen nabootsing van de engine maar de engine
 * zelf (D-19), en de suite draait in Node en in Chromium (ontwerp 5.9).
 *
 * **Vier regels die overal gelden.**
 *
 * 1. Een leeg antwoord laat alleen de lege kant kloppen: "is niet ingevuld", "heeft geen rijen" en "is niet
 *    akkoord". Ook "is niet" en "is Nee" kloppen dan niet; wie niets koos heeft niet "nee" gekozen.
 * 2. Een waarde uit het dossier die er niet is (`onbeschikbaar`, R-63) is leeg (12.31, keuze 4). Anders
 *    lekt het verschil dat R-63 verbergt.
 * 3. Tekst vergelijkt zonder hoofdletters en zonder spaties aan de randen (12.31, keuze 8).
 *    `toLowerCase` en niet `toLocaleLowerCase`: de uitkomst mag niet van de taal van de machine afhangen.
 * 4. Een datum is een string `jjjj-mm-dd` en wordt nooit een lokale `Date`. Een tijdstip met zone telt op
 *    zijn dag in UTC; zonder zone telt de dag zoals hij er staat. Zo geeft de browser in Amsterdam
 *    dezelfde uitkomst als de pod in UTC.
 *
 * Een verborgen bronveld laat elke voorwaarde niet kloppen, ook de lege kant; dat regelt `visibility.ts`,
 * want alleen die weet wat er zichtbaar is.
 *
 * **Een patroon wijkt af van regel 3** (besluit van 1 november 2026, eisenboek 12.43). "Voldoet aan
 * patroon" en "voldoet niet aan patroon" toetsen de tekst zoals hij staat, met hoofdletters en spaties aan
 * de randen, en het patroon geldt voor de hele waarde: dezelfde betekenis als de controle `regexVorm`, met
 * dezelfde matcher (`pattern.ts`). Geen uitkomst, door een gebroken patroon of een tekst boven de 10.000
 * tekens, laat beide niet kloppen.
 */

/** De soorten bron waarop een voorwaarde rekent. */
export const CONDITION_KINDS = [
  'keuze',
  'meerkeuze',
  'janee',
  'akkoord',
  'vinkje',
  'getal',
  'bedrag',
  'datum',
  'tekst',
  'trefwoorden',
  'opgemaakt',
  'bijlage',
  'rijen',
] as const
export type ConditionKind = (typeof CONDITION_KINDS)[number]

const NUMBER_OPERATORS: readonly ConditionOperator[] = [
  'gelijkAan',
  'groterDan',
  'minstens',
  'kleinerDan',
  'hoogstens',
  'tussen',
  'ingevuld',
  'nietIngevuld',
]

/**
 * Welke operatoren bij welke soort bron passen, in de volgorde waarin het paneel ze aanbiedt; de eerste is
 * de standaard. Een `Record`, zodat een soort die erbij komt de compilatie laat omvallen.
 *
 * Bij een aankruisvak zonder opties, een bijlage en opgemaakte tekst zijn het "ingevuld" en "niet
 * ingevuld" onder een eigen naam in het paneel ("is aangevinkt", "is toegevoegd"); in de definitie is het
 * dezelfde vraag, en dus dezelfde operator.
 */
export const OPERATOR_FITS: Record<ConditionKind, readonly ConditionOperator[]> = {
  keuze: ['is', 'isNiet', 'ingevuld', 'nietIngevuld'],
  meerkeuze: ['bevatEenVan', 'bevatAlle', 'bevatGeenVan', 'ingevuld', 'nietIngevuld'],
  janee: ['isJa', 'isNee'],
  akkoord: ['isAkkoord', 'isNietAkkoord'],
  vinkje: ['ingevuld', 'nietIngevuld'],
  getal: NUMBER_OPERATORS,
  bedrag: NUMBER_OPERATORS,
  datum: ['voor', 'opOfVoor', 'na', 'opOfNa', 'tussen', 'ingevuld', 'nietIngevuld'],
  tekst: ['ingevuld', 'nietIngevuld', 'bevat', 'gelijkAan', 'voldoetAanPatroon', 'voldoetNietAanPatroon'],
  trefwoorden: ['bevat', 'ingevuld', 'nietIngevuld'],
  opgemaakt: ['ingevuld', 'nietIngevuld'],
  bijlage: ['ingevuld', 'nietIngevuld'],
  rijen: ['minstensRijen', 'geenRijen'],
}

/**
 * De operatoren die het paneel onder Geavanceerd zet (D-17, eisenboek 12.43): een patroon op tekst. Ze staan
 * achteraan in `OPERATOR_FITS.tekst`, zodat de standaard voor een nieuwe voorwaarde op tekst geen patroon is.
 */
export const ADVANCED_OPERATORS: readonly ConditionOperator[] = ['voldoetAanPatroon', 'voldoetNietAanPatroon']

export function operatorFits(kind: ConditionKind, operator: ConditionOperator): boolean {
  return OPERATOR_FITS[kind].includes(operator)
}

/** Het type van een eigenschap in een contract, zoals de catalogus van verwijsbare paden het draagt. */
export interface ContractType {
  readonly type?: string
  readonly format?: string
}

/**
 * De soort bron van een verwijzing, uit het type en het formaat van het contract (R-54). Een groep of een
 * lijst heeft er geen: daar valt niets op te toetsen.
 */
export function referenceKind(contract: ContractType | undefined): ConditionKind | undefined {
  if (contract === undefined) return undefined
  if (contract.format === 'amount') return 'bedrag'
  if (contract.format === 'date' || contract.format === 'date-time') return 'datum'
  switch (contract.type) {
    case 'number':
    case 'integer':
      return 'getal'
    case 'boolean':
      return 'janee'
    case 'string':
      return 'tekst'
    default:
      return undefined
  }
}

function hasOptions(field: DraftField | Field): boolean {
  return 'opties' in field && Array.isArray(field.opties) && field.opties.length > 0
}

/**
 * De soort bron van een veld, of niets als het geen antwoord heeft om op te toetsen: een vast onderdeel,
 * een melding, een criterium (dat staat in de toetslaag en heeft een oordeel, geen antwoord).
 *
 * Een verwijzing rekent op het type uit het contract. In een gepubliceerde definitie staat het formaat
 * bevroren op de bron (`freezeReferenceFormats`); in een concept nog niet, dan geeft de aanroeper het type
 * uit de catalogus of de registry mee.
 */
export function conditionKindOf(field: DraftField | Field, contract?: ContractType): ConditionKind | undefined {
  switch (field.control) {
    case 'radio':
    case 'dropdown':
      return 'keuze'
    case 'lookup':
      return hasOptions(field) ? 'keuze' : 'tekst'
    case 'multiselect':
      return 'meerkeuze'
    case 'checkbox':
      return hasOptions(field) ? 'meerkeuze' : 'vinkje'
    case 'toggle':
      return field.akkoord === true ? 'akkoord' : 'janee'
    case 'number':
    case 'slider':
    case 'rating':
      return 'getal'
    case 'currency':
      return 'bedrag'
    case 'date':
    case 'datetime':
      return 'datum'
    case 'text':
    case 'textarea':
    case 'url':
    case 'email':
    case 'phone':
      return 'tekst'
    case 'tag':
      return 'trefwoorden'
    case 'richtext':
      return 'opgemaakt'
    case 'file':
    case 'image':
      return 'bijlage'
    case 'herhaalgroep':
      return 'rijen'
    case 'verwijzing': {
      const format = field.bron !== undefined && 'format' in field.bron ? field.bron.format : undefined
      const frozen = format === undefined ? undefined : { format }
      return referenceKind(contract ?? frozen)
    }
    default:
      return undefined
  }
}

/**
 * De waarde van een verwijzing als antwoord, zodat een voorwaarde erop met dezelfde lezers werkt als op
 * een veld (12.31, keuze 2). Een kale waarde wordt `{ waarde }`, een lijst kale waarden `{ waarden }`.
 * Niets, `null` of een object is geen antwoord en telt als leeg (keuze 4).
 */
export function answerOfReference(value: unknown): Answer | undefined {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return { waarde: value } as Answer
  if (Array.isArray(value) && value.every((item) => typeof item === 'string' || typeof item === 'number')) {
    return { waarden: [...new Set(value as (string | number)[])] } as Answer
  }
  return undefined
}

/** Tekst om te vergelijken: zonder hoofdletters en zonder spaties aan de randen (keuze 8). */
function normal(text: string): string {
  return text.trim().toLowerCase()
}

/** Een getal uit een antwoord; een getal als tekst uit het dossier telt mee, een woord niet. */
function numberOf(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(value)) return Number(value)
  return undefined
}

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const ZONED = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/
const UNZONED = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/

/**
 * De dag van een datum of tijdstip als `jjjj-mm-dd`, of niets. Een tijdstip met zone gaat langs UTC; dat
 * is overal dezelfde berekening, want `Date.parse` op een string met zone hangt niet af van de klok van de
 * machine. Zonder zone gaat het nooit langs `Date`, want dan zou de lokale tijd meetellen.
 */
export function dateOf(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const text = value.trim()
  if (DATE.test(text)) return text
  if (ZONED.test(text)) {
    const moment = Date.parse(text)
    return Number.isNaN(moment) ? undefined : new Date(moment).toISOString().slice(0, 10)
  }
  return UNZONED.exec(text)?.[1]
}

/** De enkelvoudige waarde van een antwoord, of undefined. */
function single(answer: Answer | undefined): unknown {
  return answer !== undefined && 'waarde' in answer ? answer.waarde : undefined
}

function compareNumbers(operator: ConditionOperator, actual: number, wanted: number, upTo?: number): boolean {
  switch (operator) {
    case 'gelijkAan':
      return actual === wanted
    case 'groterDan':
      return actual > wanted
    case 'minstens':
      return actual >= wanted
    case 'kleinerDan':
      return actual < wanted
    case 'hoogstens':
      return actual <= wanted
    case 'tussen':
      return upTo !== undefined && actual >= wanted && actual <= upTo
    default:
      return false
  }
}

function compareDates(operator: ConditionOperator, actual: string, wanted: string, upTo?: string): boolean {
  switch (operator) {
    case 'voor':
      return actual < wanted
    case 'opOfVoor':
      return actual <= wanted
    case 'na':
      return actual > wanted
    case 'opOfNa':
      return actual >= wanted
    case 'tussen':
      return upTo !== undefined && actual >= wanted && actual <= upTo
    default:
      return false
  }
}

/** Het aantal rijen met inhoud; een lege rij telt niet, zoals bij `minimumAantal` (besluit van 30 oktober 2026). */
function filledRows(answer: Answer | undefined): number {
  return (rowsOf(answer) ?? []).filter((row) => !rowIsEmpty(row)).length
}

/**
 * Of deze voorwaarde klopt bij dit antwoord. Puur: geen klok, geen taal van de machine, geen I/O (R-01e).
 *
 * Het antwoord is dat van een zichtbaar bronveld; een verborgen bron toetst `visibility.ts` zelf.
 */
export function conditionHolds(condition: Condition, answer: Answer | undefined): boolean {
  const { operator } = condition
  if (operator === 'nietIngevuld') return isEmpty(answer)
  if (operator === 'geenRijen') return filledRows(answer) === 0
  if (operator === 'isNietAkkoord') return single(answer) !== true
  if (operator === 'ingevuld') return !isEmpty(answer)
  if (operator === 'minstensRijen') {
    const wanted = numberOf(condition.waarde)
    return wanted !== undefined && filledRows(answer) >= wanted
  }
  // Vanaf hier: een leeg antwoord laat de voorwaarde niet kloppen (regel 1).
  if (isEmpty(answer)) return false
  const chosen = valuesOf(answer).map(String)

  switch (operator) {
    case 'is':
      return chosen.length > 0 && chosen.some((value) => (condition.waarden ?? []).map(String).includes(value))
    case 'isNiet':
      return chosen.length > 0 && !chosen.some((value) => (condition.waarden ?? []).map(String).includes(value))
    case 'bevatEenVan':
      return (condition.waarden ?? []).some((value) => chosen.includes(String(value)))
    case 'bevatAlle':
      return (condition.waarden ?? []).every((value) => chosen.includes(String(value)))
    case 'bevatGeenVan':
      return !(condition.waarden ?? []).some((value) => chosen.includes(String(value)))
    case 'isJa':
    case 'isAkkoord':
      return single(answer) === true
    case 'isNee':
      return single(answer) === false
    case 'bevat': {
      const term = typeof condition.waarde === 'string' ? normal(condition.waarde) : ''
      const texts = valuesOf(answer).filter((value): value is string => typeof value === 'string')
      return term !== '' && texts.some((text) => normal(text).includes(term))
    }
    case 'voldoetAanPatroon':
    case 'voldoetNietAanPatroon': {
      const actual = single(answer)
      if (typeof actual !== 'string' || typeof condition.waarde !== 'string') return false
      const fits = patternMatch(condition.waarde, actual)
      if (fits === undefined) return false
      return operator === 'voldoetAanPatroon' ? fits : !fits
    }
    case 'gelijkAan':
      if (typeof condition.waarde === 'string') {
        const actual = single(answer)
        return typeof actual === 'string' && normal(actual) === normal(condition.waarde)
      }
      break
    default:
      break
  }

  if (operator === 'voor' || operator === 'opOfVoor' || operator === 'na' || operator === 'opOfNa' || (operator === 'tussen' && typeof condition.waarde === 'string')) {
    const actual = dateOf(single(answer))
    const wanted = dateOf(condition.waarde)
    const upTo = condition.tot === undefined ? undefined : dateOf(condition.tot)
    if (actual === undefined || wanted === undefined || (operator === 'tussen' && upTo === undefined)) return false
    return compareDates(operator, actual, wanted, upTo)
  }

  // Een vergelijking van getallen. De parameter moet zelf een getal zijn: "25000" als tekst op een bedrag
  // is gebroken, en de poort meldt dat; hier gaat hij niet af.
  if (typeof condition.waarde !== 'number') return false
  if (operator === 'tussen' && typeof condition.tot !== 'number') return false
  const actual = numberOf(single(answer))
  if (actual === undefined) return false
  return compareNumbers(operator, actual, condition.waarde, typeof condition.tot === 'number' ? condition.tot : undefined)
}
